import type { RollsetListItem } from "./RollshopOperations";

export const OP_STATUS_IN_USE = "INUSE";
export const OP_STATUS_PREPARED_FOR_LINE = "PREPAREDFORLINE";

// One row of [ROLLS].[GetRollsProduction]: a pass of the rollset's barrel. IdBottom / IdTop are
// null on the rows the procedure makes up for a rollset that has no production yet.
export interface RollsProductionRow {
	IdBottom: number | null;
	IdTop: number | null;
	RollsetId: number;
	PassId: number | null;
	PassName: string | null;
	PassPosition: number | null;
	PassTypeName: string | null;
	PassShapeName: string | null;
	ScheduledTonnage: number | null;
	RolledTonnage: number | null;
	PassStatusCode: string | null;
	PassLastChangeReasonName: string | null;
}

export interface RollsetStatusChange {
	FromCode: string;
	FromName: string;
	ToCode: string;
	ToName: string;
}

export interface OperatorOption {
	Id: number;
	Name: string;
}

export interface PassStatusOption {
	Code: string;
	Name: string;
}

export interface TurnedRoll {
	position: string;
	newDiameter: number;
	timeToCut: number | null;
}

export interface CutRollsPayload {
	rollsetId: number;
	rolls: TurnedRoll[];
	operator1Id: number | null;
	operator2Id: number | null;
	comments: string | null;
}

export interface RollsetPosition {
	/** Value [ROLLS].[CutRolls] expects: TOP / BOTTOM on common stands, 1 to 5 otherwise. */
	position: string;
	label: string;
	rollId: number | null;
	rollName: string | null;
	diameter: number | null;
}

export type AssignmentStatus = "ASSIGNED" | "PARTIAL" | "AVAILABLE";

export const ASSIGNMENT_STATUS_LABEL: Record<AssignmentStatus, string> = {
	ASSIGNED: "Assigned",
	PARTIAL: "Partial",
	AVAILABLE: "Available",
};

// Legacy rollset.js: common stands hold a top and a bottom roll, any other stand type five.
export const getRollsetPositions = (
	rollset: RollsetListItem,
): RollsetPosition[] => {
	const isCommon = !rollset.StandType || rollset.StandType === "COMMON";
	if (isCommon) {
		return [
			{
				position: "TOP",
				label: "Top",
				rollId: rollset.TopRollId,
				rollName: rollset.TopRollName,
				diameter: rollset.TopRollDiameter,
			},
			{
				position: "BOTTOM",
				label: "Bottom",
				rollId: rollset.BottomRollId,
				rollName: rollset.BottomRollName,
				diameter: rollset.BottomRollDiameter,
			},
		];
	}
	return [
		[rollset.TopRollId, rollset.TopRollName, rollset.TopRollDiameter],
		[rollset.BottomRollId, rollset.BottomRollName, rollset.BottomRollDiameter],
		[rollset.Roll3Id, rollset.Roll3Name, rollset.Roll3Diameter],
		[rollset.Roll4Id, rollset.Roll4Name, rollset.Roll4Diameter],
		[rollset.Roll5Id, rollset.Roll5Name, rollset.Roll5Diameter],
	].map(([rollId, rollName, diameter], index) => ({
		position: String(index + 1),
		label: String(index + 1),
		rollId: rollId as number | null,
		rollName: rollName as string | null,
		diameter: diameter as number | null,
	}));
};

// The Rolls column of the list: every position in order, "-" where no roll is assigned.
export const rollsOf = (rollset: RollsetListItem) =>
	getRollsetPositions(rollset)
		.map((p) => p.rollName ?? "-")
		.join(" - ");

export const getAssignmentStatus = (
	rollset: RollsetListItem,
): AssignmentStatus => {
	const positions = getRollsetPositions(rollset);
	const assigned = positions.filter((p) => p.rollId != null).length;
	if (assigned === positions.length) return "ASSIGNED";
	return assigned > 0 ? "PARTIAL" : "AVAILABLE";
};

export const formatDiameter = (value: number | null | undefined) =>
	value == null ? "-" : value.toFixed(3);

// "1-3+1-4" before "1-11+1-12": digit runs compare as numbers.
export const compareRollsetNames = (a: string, b: string) =>
	a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

// Legacy CAT.FindRollsets matches the rollset name or the name of any of its rolls; its typeahead
// lists the names that start with the text first. 0 = name starts with it, 1 = name contains it,
// 2 = a roll contains it, null = no match.
export const rollsetSearchRank = (
	row: RollsetListItem,
	text: string,
): number | null => {
	const needle = text.trim().toLowerCase();
	if (!needle) return null;
	const name = row.Name?.toLowerCase() ?? "";
	if (name.startsWith(needle)) return 0;
	if (name.includes(needle)) return 1;
	const rolls = [
		row.TopRollName,
		row.BottomRollName,
		row.Roll3Name,
		row.Roll4Name,
		row.Roll5Name,
	];
	return rolls.some((roll) => roll?.toLowerCase().includes(needle)) ? 2 : null;
};
